// frontend/src/pages/ImportPaiements.js
// Outil admin : récupérer les paiements historiques (Stripe, SumUp) faits
// avant la mise en place de la gestion des cotisations dans l'app, les
// associer aux membres, et les importer comme cotisations payées.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, Button, Table, Thead, Tbody, Tr, Th, Td, Badge,
  Select, Checkbox, useToast, Spinner, Flex, Tabs, TabList, TabPanels,
  Tab, TabPanel, Alert, AlertIcon, HStack, TableContainer,
} from '@chakra-ui/react';
import { apiService } from '../services/apiService';
import MemberSelect from '../components/MemberSelect';

function ImportTable({ provider, transactions, loading, onFetch, types, members, rowState, setRowState }) {
  const selectable = transactions.filter(t => !t.deja_importe);
  const nbSelected = Object.values(rowState).filter(r => r.selected).length;

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Text color="gray.600">
          {transactions.length > 0
            ? `${transactions.length} transaction(s) trouvée(s), ${selectable.length} importable(s)`
            : 'Aucune transaction chargée'}
        </Text>
        <Button colorScheme="blue" onClick={onFetch} isLoading={loading}>
          Charger les paiements {{ stripe: 'Stripe', sumup: 'SumUp', paypal: 'PayPal' }[provider]}
        </Button>
      </HStack>

      {transactions.length > 0 && (
        <TableContainer maxH="60vh" overflowY="auto">
          <Table size="sm">
            <Thead bg="gray.50" position="sticky" top={0}>
              <Tr>
                <Th></Th>
                <Th>Date</Th>
                <Th>Montant</Th>
                <Th>Payeur</Th>
                <Th>Membre associé</Th>
                <Th>Type de cotisation</Th>
                <Th>Statut</Th>
              </Tr>
            </Thead>
            <Tbody>
              {transactions.map((t) => {
                const state = rowState[t.transaction_id] || {};
                return (
                  <Tr key={t.transaction_id} opacity={t.deja_importe ? 0.4 : 1}>
                    <Td>
                      <Checkbox
                        isDisabled={t.deja_importe}
                        isChecked={!!state.selected}
                        onChange={(e) => setRowState(t.transaction_id, { selected: e.target.checked })}
                      />
                    </Td>
                    <Td>{t.date}</Td>
                    <Td fontWeight="bold">{t.montant} {t.devise?.toUpperCase()}</Td>
                    <Td fontSize="sm">
                      {t.email ? <Text>{t.nom_payeur || t.email}<br /><Text as="span" color="gray.400" fontSize="xs">{t.email}</Text></Text> : <Text color="gray.400">Inconnu</Text>}
                    </Td>
                    <Td minW="220px">
                      <MemberSelect
                        members={members}
                        value={state.user_id ?? t.matched_user_id ?? ''}
                        onChange={(id) => setRowState(t.transaction_id, { user_id: id })}
                        placeholder="Choisir un membre..."
                      />
                      {t.matched_user_id && !state.user_id && (
                        <Badge colorScheme="green" fontSize="0.6em" mt={1}>Associé par email</Badge>
                      )}
                    </Td>
                    <Td minW="180px">
                      <Select
                        size="sm"
                        placeholder="Choisir..."
                        value={state.type_id || ''}
                        onChange={(e) => setRowState(t.transaction_id, { type_id: e.target.value })}
                      >
                        {types.map(ty => <option key={ty.id} value={ty.id}>{ty.nom}</option>)}
                      </Select>
                    </Td>
                    <Td>
                      {t.deja_importe
                        ? <Badge colorScheme="gray">Déjà importé</Badge>
                        : <Badge colorScheme="orange">À traiter</Badge>}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      <Text mt={3} fontSize="sm" color="gray.500">{nbSelected} ligne(s) sélectionnée(s)</Text>
    </Box>
  );
}

const CATEGORIES_DEPENSES = [
  { value: 'location', label: 'Location de l\'église' },
  { value: 'chauffage', label: 'Chauffage / Électricité' },
  { value: 'sainte_cene', label: 'Sainte Cène' },
  { value: 'informatique', label: 'Matériel informatique' },
  { value: 'fournitures', label: 'Fournitures de bureau' },
  { value: 'entretien', label: 'Entretien / Nettoyage' },
  { value: 'communication', label: 'Communication' },
  { value: 'transport', label: 'Transport' },
  { value: 'internet', label: 'Internet' },
  { value: 'seminaire', label: 'Séminaire' },
  { value: 'sonorisation', label: 'Sonorisation' },
  { value: 'autres', label: 'Autres dépenses' },
];

function ExpenseImportTable({ transactions, loading, onFetch, rowState, setRowState }) {
  const selectable = transactions.filter(t => !t.deja_importe);
  const nbSelected = Object.values(rowState).filter(r => r.selected).length;

  return (
    <Box mt={10}>
      <HStack justify="space-between" mb={4}>
        <Box>
          <Text fontWeight="bold">💸 Sorties d'argent PayPal (à ventiler dans Dépenses)</Text>
          <Text fontSize="sm" color="gray.600">
            {transactions.length > 0
              ? `${transactions.length} sortie(s) trouvée(s), ${selectable.length} importable(s)`
              : 'Aucune sortie chargée'}
          </Text>
        </Box>
        <Button colorScheme="orange" variant="outline" onClick={onFetch} isLoading={loading}>
          Recharger
        </Button>
      </HStack>

      {transactions.length > 0 && (
        <TableContainer maxH="50vh" overflowY="auto">
          <Table size="sm">
            <Thead bg="gray.50" position="sticky" top={0}>
              <Tr>
                <Th></Th>
                <Th>Date</Th>
                <Th>Montant</Th>
                <Th>Bénéficiaire</Th>
                <Th>Catégorie</Th>
                <Th>Description</Th>
                <Th>Statut</Th>
              </Tr>
            </Thead>
            <Tbody>
              {transactions.map((t) => {
                const state = rowState[t.transaction_id] || {};
                return (
                  <Tr key={t.transaction_id} opacity={t.deja_importe ? 0.4 : 1}>
                    <Td>
                      <Checkbox
                        isDisabled={t.deja_importe}
                        isChecked={!!state.selected}
                        onChange={(e) => setRowState(t.transaction_id, { selected: e.target.checked })}
                      />
                    </Td>
                    <Td>{t.date}</Td>
                    <Td fontWeight="bold">{t.montant} {t.devise?.toUpperCase()}</Td>
                    <Td fontSize="sm" color="gray.600">{t.beneficiaire || '—'}</Td>
                    <Td minW="180px">
                      <Select
                        size="sm"
                        placeholder="Choisir..."
                        value={state.categorie || ''}
                        onChange={(e) => setRowState(t.transaction_id, { categorie: e.target.value })}
                      >
                        {CATEGORIES_DEPENSES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </Select>
                    </Td>
                    <Td minW="180px" fontSize="sm">{t.description}</Td>
                    <Td>
                      {t.deja_importe
                        ? <Badge colorScheme="gray">Déjà importé</Badge>
                        : <Badge colorScheme="orange">À traiter</Badge>}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      <Text mt={3} fontSize="sm" color="gray.500">{nbSelected} ligne(s) sélectionnée(s)</Text>
    </Box>
  );
}

export default function ImportPaiements() {
  const toast = useToast();
  const [types, setTypes] = useState([]);
  const [members, setMembers] = useState([]);

  const [stripeTx, setStripeTx] = useState([]);
  const [sumupTx, setSumupTx] = useState([]);
  const [paypalTx, setPaypalTx] = useState([]);
  const [paypalDepenses, setPaypalDepenses] = useState([]);
  const [loadingStripe, setLoadingStripe] = useState(false);
  const [loadingSumup, setLoadingSumup] = useState(false);
  const [loadingPaypal, setLoadingPaypal] = useState(false);
  const [rowState, setRowStateRaw] = useState({});
  const [expenseRowState, setExpenseRowStateRaw] = useState({});
  const [importing, setImporting] = useState(false);
  const [importingExpenses, setImportingExpenses] = useState(false);

  const setRowState = useCallback((txId, patch) => {
    setRowStateRaw(prev => ({ ...prev, [txId]: { ...prev[txId], ...patch } }));
  }, []);

  const setExpenseRowState = useCallback((txId, patch) => {
    setExpenseRowStateRaw(prev => ({ ...prev, [txId]: { ...prev[txId], ...patch } }));
  }, []);

  useEffect(() => {
    apiService.makeRequest('/contributions/types').then(setTypes).catch(() => {});
    apiService.makeRequest('/members/annuaire').then(d => setMembers(d.membres || [])).catch(() => {});
  }, []);

  const fetchStripe = async () => {
    setLoadingStripe(true);
    try {
      const data = await apiService.makeRequest('/payment-import/stripe');
      setStripeTx(data.transactions || []);
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setLoadingStripe(false);
    }
  };

  const fetchSumup = async () => {
    setLoadingSumup(true);
    try {
      const data = await apiService.makeRequest('/payment-import/sumup');
      setSumupTx(data.transactions || []);
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setLoadingSumup(false);
    }
  };

  const fetchPaypal = async () => {
    setLoadingPaypal(true);
    try {
      const data = await apiService.makeRequest('/payment-import/paypal');
      setPaypalTx(data.transactions || []);
      setPaypalDepenses(data.depenses || []);
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setLoadingPaypal(false);
    }
  };

  const handleImportExpenses = async () => {
    const imports = paypalDepenses
      .filter(t => expenseRowState[t.transaction_id]?.selected)
      .map(t => {
        const state = expenseRowState[t.transaction_id];
        return {
          transaction_id: t.transaction_id,
          provider: t.provider,
          categorie: state.categorie,
          description: t.description,
          montant: t.montant,
          date: t.date,
          beneficiaire: t.beneficiaire,
        };
      });

    const incomplet = imports.find(i => !i.categorie);
    if (incomplet) {
      toast({ title: 'Sélection incomplète', description: 'Chaque ligne sélectionnée doit avoir une catégorie', status: 'warning', duration: 5000 });
      return;
    }
    if (imports.length === 0) {
      toast({ title: 'Aucune ligne sélectionnée', status: 'info', duration: 3000 });
      return;
    }

    setImportingExpenses(true);
    try {
      const result = await apiService.makeRequest('/payment-import/confirm-expenses', {
        method: 'POST',
        body: JSON.stringify({ imports }),
      });
      toast({
        title: 'Import terminé',
        description: `${result.importes} dépense(s) créée(s), ${result.ignores} déjà existante(s)`,
        status: 'success', duration: 6000,
      });
      setExpenseRowStateRaw({});
      fetchPaypal();
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setImportingExpenses(false);
    }
  };

  const handleImport = async () => {
    const allTx = [...stripeTx, ...sumupTx, ...paypalTx];
    const imports = allTx
      .filter(t => rowState[t.transaction_id]?.selected)
      .map(t => {
        const state = rowState[t.transaction_id];
        return {
          transaction_id: t.transaction_id,
          provider: t.provider,
          user_id: state.user_id || t.matched_user_id,
          type_id: state.type_id,
          montant: t.montant,
          date: t.date,
        };
      });

    const incomplet = imports.find(i => !i.user_id || !i.type_id);
    if (incomplet) {
      toast({ title: 'Sélection incomplète', description: 'Chaque ligne sélectionnée doit avoir un membre et un type de cotisation', status: 'warning', duration: 5000 });
      return;
    }
    if (imports.length === 0) {
      toast({ title: 'Aucune ligne sélectionnée', status: 'info', duration: 3000 });
      return;
    }

    setImporting(true);
    try {
      const result = await apiService.makeRequest('/payment-import/confirm', {
        method: 'POST',
        body: JSON.stringify({ imports }),
      });
      toast({
        title: 'Import terminé',
        description: `${result.importes} cotisation(s) créée(s), ${result.ignores} déjà existante(s)`,
        status: 'success', duration: 6000,
      });
      setRowStateRaw({});
      fetchStripe();
      fetchSumup();
      fetchPaypal();
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box maxW="1200px" mx="auto">
      <Heading size="lg" mb={2}>📥 Import des paiements historiques</Heading>
      <Text color="gray.600" mb={6}>
        Récupère les paiements effectués via Stripe, SumUp ou PayPal avant la mise en place des
        cotisations en ligne, pour les rattacher aux comptes des membres.
      </Text>

      <Alert status="info" borderRadius="md" mb={6}>
        <AlertIcon />
        Vérifiez chaque ligne avant import : le membre associé et le type de cotisation
        doivent être corrects. Une transaction déjà importée ne peut pas l'être une seconde fois.
      </Alert>

      <Tabs colorScheme="green">
        <TabList>
          <Tab>Stripe</Tab>
          <Tab>SumUp</Tab>
          <Tab>PayPal</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <ImportTable
              provider="stripe"
              transactions={stripeTx}
              loading={loadingStripe}
              onFetch={fetchStripe}
              types={types}
              members={members}
              rowState={rowState}
              setRowState={setRowState}
            />
          </TabPanel>
          <TabPanel>
            <ImportTable
              provider="sumup"
              transactions={sumupTx}
              loading={loadingSumup}
              onFetch={fetchSumup}
              types={types}
              members={members}
              rowState={rowState}
              setRowState={setRowState}
            />
          </TabPanel>
          <TabPanel>
            <ImportTable
              provider="paypal"
              transactions={paypalTx}
              loading={loadingPaypal}
              onFetch={fetchPaypal}
              types={types}
              members={members}
              rowState={rowState}
              setRowState={setRowState}
            />

            <ExpenseImportTable
              transactions={paypalDepenses}
              loading={loadingPaypal}
              onFetch={fetchPaypal}
              rowState={expenseRowState}
              setRowState={setExpenseRowState}
            />

            {paypalDepenses.length > 0 && (
              <Flex justify="flex-end" mt={4}>
                <Button colorScheme="orange" onClick={handleImportExpenses} isLoading={importingExpenses}>
                  Importer les dépenses sélectionnées
                </Button>
              </Flex>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Flex justify="flex-end" mt={6}>
        <Button colorScheme="green" size="lg" onClick={handleImport} isLoading={importing}>
          Importer la sélection
        </Button>
      </Flex>
    </Box>
  );
}
